import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { API_BASE_URL } from 'app/constants';
import { Tour } from 'app/models/Tour';
import { VscChecklist } from 'react-icons/all';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CircleSpinner } from 'react-spinners-kit';
import { selectAllCarriers } from 'store/slices/carriersSlice';
import { selectAllColors } from 'store/slices/colorSlice';
import { selectAllDestinations } from 'store/slices/destinationsSlice';
import { requestGet } from 'utils/requests';
import ShopPackagesSheetPrint from './ShopPackagesSheetPrint';
import {
  PrintPartOrderApiResponse,
  ShopPackagesPrintModel,
  buildShopPackagesPrintModel,
} from './shopPackagesSheetModel';

interface Props {
  tour: Tour;
}

export function ShopPackagesSheets({ tour }: Props) {
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [printModel, setPrintModel] = useState<ShopPackagesPrintModel | null>(
    null,
  );

  const destinations = useSelector(selectAllDestinations);
  const carriers = useSelector(selectAllCarriers);
  const colors = useSelector(selectAllColors);

  const handleAfterPrint = useCallback(() => {
    setPrintModel(null);
  }, []);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    bodyClass: 'toprint',
    onAfterPrint: handleAfterPrint,
  });

  useEffect(() => {
    if (!printModel) return;

    const runPrint = async () => {
      try {
        if (!handlePrint) {
          throw new Error('Print handler unavailable');
        }

        await Promise.resolve(handlePrint());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to print shop package sheet:', error);
        toast.error("Erreur lors de l'impression de la feuille de colisage.");
        setPrintModel(null);
      }
    };

    void runPrint();
  }, [handlePrint, printModel]);

  const handleClick = useCallback(async () => {
    if (isLoading || printModel) return;

    setIsLoading(true);

    try {
      const [included, excluded] = (await Promise.all([
        requestGet(`${API_BASE_URL}/part-order/`, undefined, {
          tour: tour.id,
        }),
        requestGet(`${API_BASE_URL}/part-order/`, undefined, {
          tour: tour.id,
          exclude: true,
        }),
      ])) as [PrintPartOrderApiResponse[], PrintPartOrderApiResponse[]];

      const nextModel = buildShopPackagesPrintModel({
        tour,
        currentPartOrders: Array.isArray(included) ? included : [],
        excludedPartOrders: Array.isArray(excluded) ? excluded : [],
        destinations,
        carriers,
        colors,
      });

      setPrintModel(nextModel);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load shop package sheet data:', error);
      toast.error('Impossible de charger la feuille de colisage.');
    } finally {
      setIsLoading(false);
    }
  }, [
    carriers,
    colors,
    destinations,
    isLoading,
    printModel,
    tour,
  ]);

  return (
    <>
      <button
        data-tip="Imprimer la feuille de colisage"
        data-for="infos"
        type="button"
        className="leading-3 text-xs"
        disabled={isLoading}
        onClick={handleClick}
      >
        {isLoading ? (
          <CircleSpinner size={12} color="#fff" />
        ) : (
          <VscChecklist className="h-5 w-5" />
        )}
      </button>

      {printModel && (
        <div className="will-print" style={{ display: 'none' }}>
          <div ref={componentRef}>
            <ShopPackagesSheetPrint model={printModel} />
          </div>
        </div>
      )}
    </>
  );
}
